import {Hide, HStack, Show, Text, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {Trans} from 'react-i18next';
import {Fragment} from 'react/jsx-runtime';

import {isEmpty} from 'ramda';

import {EMPTY_PLACEHOLDER, suffixTestId, TestIdProps} from 'shared/web';

import {Flag} from '../../../../../components/Flag/Flag';
import {useCatalogue} from '../../../../../hooks/useCatalogue';
import i18n from '../../../../../i18n/i18n';
import {EquipmentScoreType} from '../../../../../types/MarketType';
import type {EquipmentFlagItem} from '../types/EquipmentFlagItem';
import {getFlagColorsForCategory} from '../utils/getFlagColorsForCategory';

interface EqupimentCategoryProps extends TestIdProps {
  equipmentScore?: EquipmentScoreType;
  flags: EquipmentFlagItem[];
  minShare?: number;
  maxShare?: number;
}

export function EqupimentCategory(props: EqupimentCategoryProps) {
  const {getFeatureLabel} = useCatalogue();

  const shareLabel = match([props.minShare, props.maxShare])
    .with([Pattern.nullish, Pattern.nonNullable], ([, maxShare]) => `<lt />${maxShare}%`)
    .with([Pattern.nonNullable, Pattern.nullish], ([minShare]) => `>${minShare}%`)
    .with(
      [Pattern.nonNullable, Pattern.nonNullable],
      ([minShare, maxShare]) => `${minShare}%${EMPTY_PLACEHOLDER}${maxShare}%`
    )
    .with([Pattern.nullish, Pattern.nullish], () => '?%')
    .exhaustive();

  return (
    <VStack spacing={4}>
      <Text size="xSmall" color="secondary">
        <Trans
          i18nKey="entity.overviewEquipment.labels.share"
          values={{
            share: shareLabel,
          }}
          components={{
            bold: <Text key="bold" size="xSmall" color="secondary" alternative inline />,
            lt: <Fragment key="lt">{`<`}</Fragment>,
          }}
        />
      </Text>
      <Hide when={isEmpty(props.flags)}>
        <HStack align="center" spacing={2} wrap>
          {props.flags.map((item) => {
            const colors = getFlagColorsForCategory(item.category);
            return (
              <Flag
                key={item.feature}
                backgroundColor={colors.backgroundColor}
                color={colors.color}
                label={getFeatureLabel(item.feature) ?? item.feature}
                data-testid={suffixTestId('equipmentFlag', props)}
              />
            );
          })}
        </HStack>
      </Hide>
      <Show when={isEmpty(props.flags)}>
        <Text size="xSmall" color="secondary">
          {i18n.t('entity.overviewEquipment.labels.noEquipment')}
        </Text>
      </Show>
    </VStack>
  );
}
